import { useState, useCallback } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { v4 as uuid } from 'uuid';

import { FormInstance } from 'antd/es/form';

import {
  FeedCard,
  ImageEditorImage,
  IState,
  SocialUrls,
} from '../graphql/types';
import { CardType, FeedCardImageType, QuestionType } from '../graphql/api';

import useMutation from './useMutation';
import {
  addFeedCard,
  clearEditorData,
  clearImageEditorImage,
  setFeedcardValidationFailed,
  setImageChanged,
  setImageEditorState,
  setNewImgLoadedBlob,
  updateCardProps,
  updateEditingCardProps,
} from '../actions';
import { GiideRouteParams, ImageEditorValidation } from '../types';
import {
  encodeSocialUrls,
  encodeUrl,
  fileKeyForFeedCardImage,
  fileNameIdForFeedCardImage,
  getBlobFromAvatarImage,
  getSignedUrl,
  scrollOnUpload,
  UPLOAD_BUCKET,
  uploadPostFile,
} from '../components/utils';
import { message } from 'antd';
import useUser from './useUser';

import { convertToRaw, EditorState } from 'draft-js';
import { API } from 'aws-amplify';
import useImageUploadSubscription from './useSubscription/useImageUploadSubscription';
import gql from 'graphql-tag';
import { DocumentNode } from 'graphql';

const containsEncodedComponents = x => {
  return decodeURI(x) !== decodeURIComponent(x);
};

const createFeedCard = gql`
  mutation CreateFeedCard($input: CreateFeedCardInput!) {
    createFeedCard(input: $input) {
      id
    }
  }
`;

const updateFeedCard = gql`
  mutation UpdateFeedCard($input: UpdateFeedCardInput!) {
    updateFeedCard(input: $input) {
      id
    }
  }
`;

export const uploadImageMutation: DocumentNode = gql`
  mutation CreateUserImageRecord($input: CreateOrUpdateUserImageRecordInput!) {
    createUserImageRecord(input: $input) {
      userID
      id
      imageUrl
      createdAt
      fileName
      state
      type
      feedCardId
      errorMessageDisplayToUser
    }
  }
`;

export const createImageUploadRecord = async input => {
  return API.graphql({
    query: uploadImageMutation,
    variables: { input },
  });
};

const useFeedCardForm = (
  type?: CardType | undefined,
  form?: FormInstance,
  close?: () => void
) => {
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const validationFailed = useSelector<IState, boolean>(
    state => !!state.feedcardValidationFailed
  );

  const { id } = useParams<GiideRouteParams>();
  const { user } = useUser();
  const [progress, setProgress] = useState<number>(0);
  const feedcardFrom = document.getElementById('feedCardForm');
  const feedcardFromoffsetHeight = feedcardFrom?.offsetHeight;
  const newImgLoadedBlob = useSelector<IState, string | undefined>(
    state => state.newImgLoadedBlob
  );

  const cardProps = useSelector<IState, Partial<FeedCard> | undefined>(
    state => state.cardProps,
    () => false
  );

  const editingCardProps = useSelector<IState, Partial<FeedCard> | undefined>(
    state => state.editingCardProps
  );

  const isScraping = useSelector<IState, boolean>(
    state => state.isScraping ?? false
  );

  const isScraped = useSelector<IState, boolean>(
    state => state.isInitialScrapingDone ?? false
  );

  const imageEditorValidation = useSelector<IState, ImageEditorValidation>(
    state => state.imageEditorValidation ?? { failed: false }
  );

  const dispatch = useDispatch();
  const { callMutation } = useMutation(
    cardProps?.id ? updateFeedCard : createFeedCard
  );

  const { subscribe: subscribeToImageUpload } = useImageUploadSubscription();

  const isNewResourceCardScrappedOnce =
    isScraped ||
    !(cardProps?.type === 'resource') ||
    (!isScraped && cardProps?.id);

  const uploadFile = useCallback(
    async (file, fileName?: string | null, fileNameId?: string) => {
      if (!fileName || !fileNameId) {
        fileName =
          file.name ??
          cardProps?.imageDetails?.userUploadedImageDetails?.customImageName;

        fileNameId = fileNameIdForFeedCardImage(cardProps, fileName);
      }

      const uploadKey = fileKeyForFeedCardImage(
        user!.tenantID,
        id,
        fileNameId,
        user!.userId
      );

      const data = await getSignedUrl(
        uploadKey,
        UPLOAD_BUCKET,
        'asset',
        'post'
      );

      const { url, fields } = data;

      if (fields && url) {
        const uploadedImg = await uploadPostFile(
          url,
          file,
          fields,
          setProgress
        );

        return !!uploadedImg;
      }
    },
    [user, id, cardProps]
  );

  const handleImageSelect = useCallback(
    async imageDetails => {
      cardProps?.id
        ? dispatch(
            updateEditingCardProps({
              url: null,
              imageDetails,
            })
          )
        : dispatch(
            updateCardProps({
              url: null,
              imageDetails,
            })
          );
    },
    [dispatch, cardProps?.id]
  );

  const imageEditorState = useSelector<IState, ImageEditorImage | null>(
    state => state.imageEditorImage
  );

  const updateImageCardProps = useCallback(
    (file?, firstUpload?: boolean, shouldUpload: boolean = false) => {
      const fileName =
        file.name ??
        cardProps?.imageDetails?.userUploadedImageDetails?.customImageName;

      const fileNameId = fileNameIdForFeedCardImage(cardProps, fileName);

      const key = fileKeyForFeedCardImage(
        user!.tenantID,
        id,
        fileNameId,
        user?.userId
      );

      const isResourceType = props => props?.type === 'resource';
      const isProfileType = props => props?.type === 'profile';

      const getUrl = (props, key) =>
        isResourceType(props) ? props.url : isProfileType(props) ? key : null;

      const commonPayload = {
        url: getUrl(cardProps, key),
        customImage:
          isResourceType(cardProps) || isProfileType(cardProps)
            ? key
            : undefined,
        customImageName: fileName,
        imageBlob: URL.createObjectURL(file),
        imageService: 'cloudinary',
        imageDetails: {
          url: key,
          imageTypeSelected: FeedCardImageType.userUploadedImage,
          userUploadedImageDetails: {
            customImageName: fileName,
          },
          caption: '',
          altDescription: '',
        },
        uploadedImgBlob: firstUpload ? URL.createObjectURL(file) : undefined,
      };

      if (cardProps?.id) {
        const editingPayload = {
          ...commonPayload,
          url: getUrl(editingCardProps, key),
        };
        dispatch(updateEditingCardProps(editingPayload as Partial<FeedCard>));
      } else {
        dispatch(updateCardProps(commonPayload as Partial<FeedCard>));
      }

      const feedcardFromScrollTop = feedcardFrom?.scrollTop;

      scrollOnUpload(
        type,
        feedcardFrom,
        feedcardFromoffsetHeight,
        feedcardFromScrollTop
      );

      if (shouldUpload) {
        setProgress(0);
      }
    },
    [
      cardProps,
      dispatch,
      feedcardFrom,
      feedcardFromoffsetHeight,
      id,
      type,
      user,
      editingCardProps,
    ]
  );

  const imageMimeTypeLoaded = useSelector<IState, string | undefined>(
    state => state.imageMimeTypeLoaded
  );

  const handleUpload = useCallback(
    async (
      file,
      firstUpload?: boolean,
      shouldUpload: boolean = false,
      feedCardId?: string,
      giideId?: string
    ) => {
      if (!imageEditorState) {
        return Promise.reject();
      }

      const fileName =
        cardProps?.imageDetails?.userUploadedImageDetails?.customImageName;

      const customImageUrl =
        cardProps?.type === 'resource' && cardProps.customImage
          ? cardProps.customImage
          : cardProps?.imageDetails?.url;

      const customImageUrlParts = customImageUrl?.split('/');

      const fileNameId = customImageUrlParts
        ? customImageUrlParts[customImageUrlParts.length - 1]
        : '';

      const key = fileKeyForFeedCardImage(
        user!.tenantID,
        id,
        fileNameId,
        user?.userId
      );

      if (shouldUpload) {
        const createdAt = String(Date.now());

        const originalFileExtension = imageEditorState.extension;

        // Create a record of this image with a state of 'uploading'
        const uploadImageMutationInput = {
          id: fileNameId,
          giideId: id,
          type: 'feedCard',
          feedCardId,
          userID: user?.userId,
          fileName,
          imageUrl: 'studio/' + key,
          imageService: 'cloudinary',
          createdAt, // TODO Update this
          state: 'uploading',
          originalFileExtension,
          originalFileMimeType: imageMimeTypeLoaded,
        };

        try {
          await Promise.all([
            API.graphql({
              query: uploadImageMutation,
              variables: { input: uploadImageMutationInput },
            }),

            subscribeToImageUpload(id, feedCardId),

            uploadFile(file, fileName, fileNameId).catch(() =>
              message.error('Failed uploading the image')
            ),
          ]);
        } catch (er) {
          message.error('Failed uploading the image');
        }
      }

      return true;
    },
    [
      cardProps,
      id,
      user,
      uploadFile,
      imageEditorState,
      imageMimeTypeLoaded,
      subscribeToImageUpload,
    ]
  );

  const handleRemove = useCallback(async () => {
    batch(() => {
      cardProps?.id
        ? dispatch(
            updateEditingCardProps({
              url:
                editingCardProps?.type === 'resource'
                  ? editingCardProps.url
                  : '',
              customImage: '',
              customImageName: '',
              imageBlob: cardProps?.imageUrl as string,
              uploadedImgBlob: '',
              imageDetails: null,
            })
          )
        : dispatch(
            updateCardProps({
              url: cardProps?.type === 'resource' ? cardProps.url : '',
              customImage: '',
              customImageName: '',
              imageBlob: cardProps?.imageUrl as string,
              uploadedImgBlob: '',
              imageDetails: null,
            })
          );
      dispatch(setImageEditorState(undefined));
      dispatch(setNewImgLoadedBlob(undefined));
      dispatch(setImageChanged(true));
      dispatch(clearImageEditorImage());
    });
  }, [dispatch, cardProps, editingCardProps]);

  const customValidation = useCallback(() => {
    let customValidationFailed = false;
    if (
      (cardProps?.type === CardType.graphic &&
        (cardProps.imageDetails?.url ?? '').length === 0) ||
      (cardProps?.type === CardType.profile &&
        (cardProps?.url ?? '').length === 0)
    ) {
      customValidationFailed = true;
      dispatch(setFeedcardValidationFailed(true));
    } else if (
      cardProps?.type === CardType.quiz &&
      (cardProps?.questionType === QuestionType.multipleChoiceSingleResponse ||
        !cardProps?.questionType) &&
      (!cardProps?.answerDetails?.answerOptions?.length ||
        (cardProps?.answerDetails?.answerOptions?.length ?? 0) < 2)
    ) {
      customValidationFailed = true;
      dispatch(setFeedcardValidationFailed(true));
    } else if (
      cardProps?.type === CardType.resource &&
      (cardProps?.customImage?.length === 0 || !cardProps?.customImage) &&
      cardProps.customImageName === 'unnamed_file'
    ) {
      customValidationFailed = true;
      dispatch(setFeedcardValidationFailed(true));
      message.error(
        'Missing custom image, please upload image or switch to default image.'
      );
    } else if (
      cardProps?.type === CardType.list &&
      (cardProps?.listItems?.filter(item => item).length ?? 0) < 1
    ) {
      customValidationFailed = true;
      dispatch(setFeedcardValidationFailed(true));
    } else if (imageEditorValidation?.failed) {
      customValidationFailed = true;
      dispatch(setFeedcardValidationFailed(true));
      message.error(
        'Image validation failed, please try to change image dimensions.'
      );
    } else {
      dispatch(setFeedcardValidationFailed(false));
    }
    return customValidationFailed;
  }, [cardProps, dispatch, imageEditorValidation]);

  const addFeedcard = useCallback(
    (
      feedCardInput,
      stepIndex,
      recording,
      url,
      imageUrl,
      iconUrl,
      socialUrls,
      fcId
    ) => {
      dispatch(
        addFeedCard({
          ...feedCardInput,
          stepIndex,
          recording,
          url,
          imageUrl,
          iconUrl,
          socialUrls,
        })
      );

      if (!fcId) {
        document.getElementById(feedCardInput.id)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [dispatch]
  );

  const imageHasChanged = useSelector<IState, boolean | undefined>(
    state => state.imageChanged
  );

  const saveFeedCard = useCallback(
    async (editorRef?) => {
      if (!type) {
        return;
      }

      await form?.validateFields();

      setSubmitDisabled(true);

      const customValidationFailed = customValidation();

      if (!customValidationFailed) {
        try {
          for (let cardPropKey in cardProps) {
            let cardPropVal = cardProps[cardPropKey];
            if (cardPropVal && typeof cardPropVal === 'string') {
              cardPropVal = cardPropVal.trim();
            }
            if (cardPropVal === '') {
              cardProps[cardPropKey] = null;
            }
          }

          if (cardProps) {
            const {
              recording,
              selectedWord,
              isInLibrary,
              stepIndex,
              id: fcId,
              imageBlob,
              fetchedUrl,
              url,
              imageUrl,
              iconUrl,
              socialUrls,
              htmlText,
              listItems,
              ...other
            } = cardProps;

            const encodedSocialUrls = encodeSocialUrls(
              (socialUrls as SocialUrls) ?? null
            );

            const imageDetailsURL = url || other.imageDetails?.url || null;

            const imageDetails =
              type === CardType.graphic
                ? {
                    ...other.imageDetails,
                    url:
                      !imageDetailsURL || imageDetailsURL.startsWith('http')
                        ? imageDetailsURL
                        : containsEncodedComponents(imageDetailsURL)
                        ? imageDetailsURL
                        : encodeUrl(imageDetailsURL),
                  }
                : undefined;

            const feedCardInput = {
              ...other,
              id: fcId || uuid(),
              giideId: id,
              feedCardRecordingId: fcId ? undefined : recording!.id,
              nonLibraryGiideID: isInLibrary ? id : undefined,
              type,
              questionType:
                // i encounted bug, where the questionType param was not recorded correctly, but unfortunately could not locate it
                type === CardType.quiz
                  ? other.questionType ??
                    QuestionType.multipleChoiceSingleResponse
                  : undefined,
              // resourceType:
              //   type === CardType.resource ? ResourceType.website : undefined,
              imageDetails,
              url: encodeUrl(url),
              imageUrl: encodeUrl(imageUrl),
              iconUrl: encodeUrl(iconUrl),
              socialUrls: encodedSocialUrls,
              uploadedImgBlob: undefined,
              htmlText: htmlText
                ? JSON.stringify(
                    convertToRaw((htmlText as EditorState).getCurrentContent())
                  )
                : undefined,
              listItems:
                type === CardType.list && listItems
                  ? listItems.filter(item => item)
                  : undefined,
            };

            // Feed cards with custom loaded images are uploaded here.
            // If the image was created in the Avatar Editor, it needs
            // to be fetched from the editor using editorRef
            const asyncJobs: Promise<any>[] = [];
            if (cardProps && imageHasChanged) {
              let blob: null | Blob | undefined = null;

              if (
                cardProps.type === 'graphic' &&
                cardProps.imageDetails?.imageTypeSelected ===
                  'userUploadedImage'
              ) {
                const response = await fetch(newImgLoadedBlob as string);
                blob = await response.blob();
              } else if (cardProps.type === 'profile') {
                blob = await getBlobFromAvatarImage(editorRef);
              } else if (
                cardProps.type === 'resource' &&
                cardProps.customImageName
              ) {
                blob = await getBlobFromAvatarImage(editorRef);
              }

              if (blob) {
                asyncJobs.push(
                  handleUpload(blob, true, true, feedCardInput.id)
                );
              }
            }

            asyncJobs.push(callMutation(feedCardInput));

            await Promise.all(asyncJobs);

            close?.();

            form?.resetFields();

            addFeedcard(
              feedCardInput,
              stepIndex,
              recording,
              url,
              imageUrl,
              iconUrl,
              socialUrls,
              fcId
            );
          }

          batch(() => {
            dispatch(clearEditorData());
            dispatch(clearImageEditorImage());
          });
        } catch (e) {
          message.error('Failed to save feedcard');
          setSubmitDisabled(false);
          return;
        }
      }
      setSubmitDisabled(false);
    },
    [
      callMutation,
      imageHasChanged,
      cardProps,
      close,
      form,
      id,
      type,
      customValidation,
      addFeedcard,
      dispatch,
      handleUpload,
      newImgLoadedBlob,
    ]
  );

  return {
    saveFeedCard,
    submitDisabled,
    isScraping,
    isNewResourceCardScrappedOnce,
    validationFailed,
    handleUpload,
    handleImageSelect,
    progress,
    handleRemove,
    imageEditorValidation,
    getBlobFromAvatarImage,
    updateImageCardProps,
  };
};

export default useFeedCardForm;
